import {
  AdjustableType,
  AdjustmentRuleType,
  DiscountTypeEnum,
  prisma,
} from "client";

// Bulk
export const OrderDiscountBulkEligibility = async (
  orderId: string,
  eventId: string
) => {
  try {
    const adjustmentRules = await prisma.adjustmentRule.findMany({
      where: {
        eventId,
        type: AdjustmentRuleType.DISCOUNT,
        adjustableType: AdjustableType.ORDER,
        startDate: {
          lte: new Date(),
        },
        endDate: {
          gte: new Date(),
        },
      },
      include: {
        discountAdjustments: {
          select: {
            id: true,
            stackable: true,
          },
        },
      },
    });
    if (!adjustmentRules.length) {
      return [];
    }
    const pipelines = adjustmentRules.map((rule: any) => {
      // Build conditions for the specific rule
      const ruleConditions = [];
      if (rule.conditions?.operator) {
        switch (rule.conditions.operator) {
          case "AND_MULTIPLE":
            ruleConditions.push({
              $eq: [{ $mod: ["$count", rule.conditions.quantity] }, 0],
            });
            break;
          case "ONLY":
            ruleConditions.push({
              $eq: ["$count", rule.conditions.quantity],
            });
            break;
          case "OR_GREATER":
            ruleConditions.push({
              $gte: ["$count", rule.conditions.quantity],
            });
            break;
          default:
            throw new Error(
              `Unsupported condition operator: ${rule.condition.operator}`
            );
        }
      }
      // Create a separate pipeline for this rule
      return [
        {
          $match: {
            $expr: {
              $eq: [
                "$_id",
                {
                  $toObjectId: orderId,
                },
              ],
            },
          },
        },
        {
          $lookup: {
            from: "OrderItem",
            localField: "_id",
            foreignField: "orderId",
            as: "orderItem",
          },
        },
        { $unwind: { path: "$orderItem", preserveNullAndEmptyArrays: true } },
        {
          $match: {
            $expr: {
              $in: [
                "$orderItem.ticketId",
                rule.ticketIds.map((ticketId: string) => {
                  return { $toObjectId: ticketId };
                }),
              ],
            },
          },
        },
        {
          $group: {
            _id: null, // Group by ticket ID if needed
            count: { $sum: 1 },
          },
        },
        {
          $addFields: {
            isEligible: { $and: ruleConditions }, // Use rule-specific conditions
            ruleId: rule.id,
            flatAmount: rule.flatAmount,
            percentageAmount: rule.percentageAmount,
            adjustableType: rule.adjustableType,
            type: rule.type,
            stackable: rule?.discountAdjustments?.[0]?.stackable,
          },
        },
        {
          $project: {
            _id: 0,
            isEligible: 1,
            ruleId: 1,
            flatAmount: 1,
            percentageAmount: 1,
            adjustableType: 1,
            type: 1,
            stackable: 1,
          },
        },
      ];
    });
    // Execute pipelines independently and combine results
    const eligibilitySummaries = [];
    for (const pipeline of pipelines) {
      const summary = await prisma.order.aggregateRaw({ pipeline });
      // @ts-ignore
      if (summary.length && summary?.[0]?.isEligible) {
        eligibilitySummaries.push(summary[0]);
      }
    }

    let checkStackableFalse = eligibilitySummaries.filter(
      (item: any) => item.stackable === false
    );

    if (eligibilitySummaries.length == checkStackableFalse.length) {
      // Higher value
      let higherValue = await higherValueItem(orderId, eligibilitySummaries);
      higherValue = higherValue?.filter((item) => item !== undefined);
      return { data: higherValue };
    } else {
      let checkStackableTrue = eligibilitySummaries.filter(
        (item: any) => item.stackable === true
      );
      checkStackableTrue = checkStackableTrue?.filter(
        (item) => item !== undefined
      );
      return { data: checkStackableTrue };
    }
  } catch (e: any) {
    console.error(e);
    return [];
  }
};

// Code
export const OrderDiscountCodeEligibility = async (
  orderId: string,
  code: string,
  eventId: string,
  ticketIds: any[]
) => {
  try {
    const appliedAdjustment = await prisma.appliedAdjustment.findMany({
      where: {
        adjustableId: orderId,
        eventId: eventId,
      },
      include: {
        adjustmentRule: {
          include: {
            discountAdjustments: {
              where: {
                discountType: DiscountTypeEnum.CODE,
              },
              select: {
                stackable: true,
                code: true,
              },
            },
          },
        },
      },
    });

    const getCode = await prisma.adjustmentRule.findFirst({
      where: {
        discountAdjustments: {
          some: {
            code: code,
          },
        },
      },
      include: {
        discountAdjustments: true,
      },
    });

    let checkIfCodeIsUsed = appliedAdjustment.filter(
      (item) =>
        item?.adjustmentRule?.discountAdjustments?.[0]?.code === code &&
        item?.adjustableId === orderId
    );

    if (!getCode) {
      throw new Error("Invalid discount code");
    }

    if (checkIfCodeIsUsed.length > 0) {
      throw new Error("Code is already used");
    }

    const whereCondition: any = {
      eventId,
      type: AdjustmentRuleType.DISCOUNT,
      adjustableType: getCode?.adjustableType,
      startDate: {
        lte: new Date(),
      },
      endDate: {
        gte: new Date(),
      },
      discountAdjustments: {
        some: {
          code: code,
        },
      },
    };

    // Only add ticketIds filter if adjustableType is OrderItem
    if (getCode?.adjustableType === AdjustableType.ORDER_ITEM) {
      whereCondition.ticketIds = {
        hasSome: ticketIds,
      };
    }

    const adjustmentRules = await prisma.adjustmentRule.findFirst({
      where: whereCondition,
      include: {
        discountAdjustments: {
          select: {
            id: true,
            stackable: true,
            code: true,
            discountType: true,
          },
        },
      },
    });

    if (!adjustmentRules) {
      return [];
    }

    if (appliedAdjustment.length == 0) {
      return { data: adjustmentRules };
    } else {
      let checkStackableTrue: any[] = [];
      appliedAdjustment?.map((item: any) => {
        checkStackableTrue.push(
          item?.adjustmentRule?.discountAdjustments?.[0]?.stackable
        );
      });
      if (
        checkStackableTrue.includes(false) == false &&
        getCode?.discountAdjustments?.[0]?.stackable == true
      ) {
        return { data: adjustmentRules };
      } else {
        return [];
      }
    }
  } catch (e: any) {
    console.error(e);
    return [];
  }
};

// Charge
export const OrderChargeEligibility = async (
  eventId: string,
  ticketIds: any[],
  paymentMethodIds: any[]
) => {
  try {
    const adjustmentRules = await prisma.adjustmentRule.findMany({
      where: {
        eventId,
        type: AdjustmentRuleType.CHARGE,
        startDate: {
          lte: new Date(),
        },
        endDate: {
          gte: new Date(),
        },
      },
      include: {
        chargeAdjustments: {
          select: {
            id: true,
            taxable: true,
          },
        },
      },
    });

    if (!adjustmentRules.length) {
      return [];
    }

    let result: any[] = [];

    let chargeForOrder = adjustmentRules.filter(
      (item) =>
        item.ticketIds.length === 0 &&
        item.adjustableType === AdjustableType.ORDER
    );

    let chargeForOrderItem = adjustmentRules.filter(
      (item) =>
        item.ticketIds.length > 0 &&
        item.adjustableType === AdjustableType.ORDER_ITEM &&
        item.ticketIds.some((ticketId) => ticketIds.includes(ticketId))
    );

    let chargeForPayment = adjustmentRules.filter(
      (item) =>
        item.paymentMethods.length > 0 &&
        item.adjustableType === AdjustableType.PAYMENT &&
        item.paymentMethods.some((paymentMethodId) =>
          paymentMethodIds.includes(paymentMethodId)
        )
    );

    // Push all filtered results into the result array.
    result.push(...chargeForOrder, ...chargeForOrderItem, ...chargeForPayment);

    return { data: result };
  } catch (e: any) {
    console.error(e);
    return [];
  }
};

const higherValueItem = async (id: string, discounts: any) => {
  const order = await prisma.order.findUnique({
    where: {
      id,
    },
  });

  // Assuming the total amount is $100 (for example)
  const totalAmount = order?.total;
  const calculateDiscountValue = (discount: any, totalAmount: any) => {
    if (!discount.isEligible) return 0;

    const flatDiscount = discount.flatAmount;
    const percentageDiscount = (discount.percentageAmount / 100) * totalAmount;

    return flatDiscount + percentageDiscount;
  };

  // Find the item with the highest discount value
  const bestDiscountItem = discounts.reduce(
    (bestItem: any, currentItem: any) => {
      const bestDiscountValue = calculateDiscountValue(bestItem, totalAmount);
      const currentDiscountValue = calculateDiscountValue(
        currentItem,
        totalAmount
      );

      return currentDiscountValue > bestDiscountValue ? currentItem : bestItem;
    },
    discounts[0]
  );

  const result: any[] = [];
  result.push(bestDiscountItem);
  return result;
};
